/**
 * The E-Mail-Absender page's rules, as pure functions (T1b).
 *
 * Everything the page decides without React lives here so it can be asked
 * directly: which fields exist and in which order, what counts as a change,
 * what a save sends, what a refusal says in German, and how a history entry
 * reads. The page and its components only render what these return.
 *
 * THE BACKEND IS THE AUTHORITY (T1a). It trims, turns blank into `null`,
 * validates each address as exactly one address and answers the stored truth.
 * Nothing here normalises beyond that: `canonical` is the server's own blank →
 * `null` and trim rule, applied so that a field the server would store unchanged
 * does not count as a change. There is no lowercasing — the server lowercases a
 * domain itself and answers the result, which then becomes the new baseline.
 *
 * THE CLIENT CHECKS ARE A FIRST LOOK, NOT THE RULE. `validateForm` refuses only
 * what is obviously not a single plain address — a list, a display-name form,
 * something without `@` — so the person learns it before a round trip. Whatever
 * passes still goes to the server, whose 422 is shown per field.
 */

/** The four writable fields, in the order the page shows them. */
export const FIELDS = [
  "sender_name",
  "sender_email",
  "reply_to",
  "test_recipient_email",
];

export const FIELD_LABELS = {
  sender_name: "Absendername",
  sender_email: "Absender-E-Mail",
  reply_to: "Reply-To",
  test_recipient_email: "Test-E-Mail-Adresse",
};

export const EMAIL_FIELDS = new Set([
  "sender_email",
  "reply_to",
  "test_recipient_email",
]);

/**
 * The column widths (migrations 170 and 190). `sender_name` has no length rule
 * in the request schema, so without this limit a longer name would reach the
 * database and come back as a generic 500 instead of a sentence.
 */
export const MAX_LENGTH = {
  sender_name: 200,
  sender_email: 320,
  reply_to: 320,
  test_recipient_email: 320,
};

export const LOAD_ERROR = "Die E-Mail-Einstellungen konnten nicht geladen werden.";
export const ACTIVITY_ERROR = "Der Änderungsverlauf konnte nicht geladen werden.";
export const SAVE_FAILED =
  "Die E-Mail-Einstellungen konnten nicht gespeichert werden. Bitte später erneut versuchen.";
export const SAVE_INVALID =
  "Die Einstellungen wurden nicht gespeichert. Bitte die markierten Felder prüfen.";
export const SAVE_REFUSED = "Die Eingaben wurden vom Server abgelehnt.";
export const SESSION_EXPIRED = "Sitzung abgelaufen. Bitte neu anmelden.";
/** The server's own refusal in support mode (T1a, `_require_admin_actor`). */
export const SUPPORT_REFUSAL =
  "Im Support-Modus können die E-Mail-Einstellungen nicht geändert werden.";
export const SAVE_FORBIDDEN =
  "Die E-Mail-Einstellungen können mit deinen Berechtigungen nicht geändert werden.";
export const SAVE_SUCCESS = "E-Mail-Einstellungen gespeichert.";

const INVALID_ADDRESS = "Bitte eine gültige E-Mail-Adresse eingeben, z. B. name@firma.ch.";
const SINGLE_ADDRESS = "Bitte genau eine E-Mail-Adresse eingeben, keine Liste.";
const PLAIN_ADDRESS = "Bitte nur die E-Mail-Adresse eingeben, ohne Namen.";
const CHECK_INPUT = "Bitte diese Eingabe prüfen.";

/** pydantic's prefix on a refusal the server worded itself (in German). */
const SERVER_WORDED = "Value error, ";

/**
 * The value as the server would store it: `null` for nothing or blank, else the
 * trimmed text. The same rule as the backend's `_empty_to_none`.
 */
export function canonical(value) {
  if (typeof value !== "string") return null;
  const trimmed = value.trim();
  return trimmed === "" ? null : trimmed;
}

/** The settings object as the form's text fields; `null` shows as empty. */
export function settingsToForm(settings) {
  const form = {};
  FIELDS.forEach((field) => {
    const value = settings?.[field];
    form[field] = typeof value === "string" ? value : "";
  });
  return form;
}

/**
 * Exactly the fields that changed, each as the server would store it — a
 * cleared field as `null`. Only the four writable fields are ever looked at, so
 * nothing else the read answered (`birthday_email_enabled`, the timestamps) can
 * leak into a write.
 */
export function buildPatch(form, initial) {
  const patch = {};
  FIELDS.forEach((field) => {
    const next = canonical(form?.[field]);
    if (next !== canonical(initial?.[field])) patch[field] = next;
  });
  return patch;
}

export function isDirty(form, initial) {
  return Object.keys(buildPatch(form, initial)).length > 0;
}

/** Whether two settings objects store the same four values. */
export function sameStoredValues(a, b) {
  return FIELDS.every((field) => canonical(a?.[field]) === canonical(b?.[field]));
}

function addressProblem(value) {
  if (/[,;]/.test(value)) return SINGLE_ADDRESS;
  if (/[<>"]/.test(value)) return PLAIN_ADDRESS;
  // One `@`, something on both sides, a dot in the domain and no whitespace —
  // the obvious shape only. Anything subtler is the server's call.
  if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) return INVALID_ADDRESS;
  return "";
}

/**
 * First-look problems with the fields that WOULD BE SENT, as `{ field: text }`.
 * An unchanged stored value is never judged here: it is the server's truth, and
 * a save of another field must not be blocked by it.
 */
export function validateForm(form, initial) {
  const errors = {};
  const patch = buildPatch(form, initial);
  Object.keys(patch).forEach((field) => {
    const value = patch[field];
    if (value === null) return;
    if (value.length > MAX_LENGTH[field]) {
      errors[field] = `${FIELD_LABELS[field]} darf höchstens ${MAX_LENGTH[field]} Zeichen lang sein.`;
      return;
    }
    if (EMAIL_FIELDS.has(field)) {
      const problem = addressProblem(value);
      if (problem) errors[field] = problem;
    }
  });
  return errors;
}

/**
 * The server's field refusals in German. A refusal the server worded itself
 * comes with pydantic's `Value error, ` prefix and is shown without it; the
 * address check's own text is English, so an address field gets a German
 * sentence instead of it.
 */
export function fieldErrorsFromError(err) {
  const errors = {};
  const list = Array.isArray(err?.fieldErrors) ? err.fieldErrors : [];
  list.forEach(({ field, msg }) => {
    if (!FIELDS.includes(field) || errors[field]) return;
    if (typeof msg === "string" && msg.startsWith(SERVER_WORDED)) {
      errors[field] = msg.slice(SERVER_WORDED.length);
    } else if (EMAIL_FIELDS.has(field)) {
      errors[field] = INVALID_ADDRESS;
    } else {
      errors[field] = CHECK_INPUT;
    }
  });
  return errors;
}

/**
 * What a failed save says, always in German. The support-mode refusal is the
 * server's own sentence and is shown as it came — the server decided it, not
 * the page. Any other 403 is the framework's English ("Not enough permissions"
 * for a role that changed while the page was open, "Not authenticated" without
 * a token) and becomes a German sentence, like a 500's "Internal Server Error"
 * and a dropped connection's browser message.
 */
export function saveErrorMessage(err) {
  const status = err?.status;
  if (status === 403) {
    const message = typeof err?.message === "string" ? err.message.trim() : "";
    return message === SUPPORT_REFUSAL ? SUPPORT_REFUSAL : SAVE_FORBIDDEN;
  }
  if (status === 422) {
    return Object.keys(fieldErrorsFromError(err)).length ? SAVE_INVALID : SAVE_REFUSED;
  }
  if (status === 401) return SESSION_EXPIRED;
  return SAVE_FAILED;
}

/**
 * The changes of one history entry, in the page's field order — the server
 * sorts `changed_fields` alphabetically. A side that was not set is `null`.
 */
export function activityChanges(entry) {
  const changed = Array.isArray(entry?.changed_fields) ? entry.changed_fields : [];
  const before = entry?.old_values || {};
  const after = entry?.new_values || {};
  return FIELDS.filter((field) => changed.includes(field)).map((field) => ({
    field,
    label: FIELD_LABELS[field],
    before: typeof before[field] === "string" ? before[field] : null,
    after: typeof after[field] === "string" ? after[field] : null,
  }));
}
